// Shared player redemption hooks (plan §12). Desktop dialogs and mobile sheets consume
// the SAME data layer so there is one source of truth for options, limits and history.
import { useCallback, useEffect, useState } from "react";
import { payoutsApi } from "@/lib/payouts";

export function usePayoutOptions() {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    return payoutsApi
      .options()
      .then((data) => {
        setOptions(data);
        setError(null);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { options, loading, error, reload };
}

// True when the server replied with a 503 payouts_unavailable (domain OFF) — the only
// case where the legacy wallet path should be used. A non-503 failure means the domain
// may be enabled but erroring, so we must NOT silently route to legacy mutations.
export function isDomainOff(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.detail?.code;
  return status === 503 && code === "payouts_unavailable";
}

// Lightweight signal for legacy wallet code: is the new payout domain enabled?
// Enabled when /payouts/options loads, OR fails with a non-503 error (domain mounted but
// erroring — keep the new flow, never fall back to legacy). Only a 503 => domain off.
export function usePayoutDomainEnabled() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    payoutsApi
      .options()
      .then(() => { if (alive) setEnabled(true); })
      .catch((e) => { if (alive) setEnabled(!isDomainOff(e)); })  // non-503 => keep new flow
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { enabled, loading };
}

export function useRedemptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    return payoutsApi
      .history()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const cancel = useCallback(
    async (reference, reason) => {
      await payoutsApi.cancel(reference, reason);
      await reload();
    },
    [reload]
  );

  return { items, loading, reload, cancel };
}
